import React, { useMemo, useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Paginator } from "primereact/paginator";
import { AdvanceFilter } from "../../M_Components/M_Table/advanceFilter/AdvanceFilter";

import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

import TableHeader from "../tableHelpers/TableHeader";
import ColumnMenu from "../tableHelpers/ColumnMenu";
import ViewsMenu from "../tableHelpers/ViewMenu";

import {
  headerCellStyle,
  cellStyle as tdStyle,
} from "../tableHelpers/TableStyleUtils";

import { useTableFilters } from "../tableHelpers/useTableFilters";

import "./TenderSummaryTable.css";

/**
 * TenderSummaryTable
 * =================================================================================================
 * Lane-level summary view for a Tender.
 *
 * Responsibilities:
 * - Render summary rows already prepared by the parent page.
 * - Support client-side filtering, pagination, column visibility and Excel export.
 * - Respect Spot vs Non-Spot column visibility.
 *
 * Non-responsibilities:
 * - Does not compute totals.
 * - Does not merge snapshot data with accepted quote aggregates.
 * - Does not apply Tender business rules.
 *
 * Expected row shape:
 * - laneId
 * - origin
 * - destination
 * - totalQuotes
 * - totalBidValue
 * - maxBidValue
 * - minBidValue
 * - totalLoads
 * - maxLoads
 * - minLoads
 * - maxEstimatedTransitDays
 * - minEstimatedTransitDays
 * - isSatisfied
 * - satisfiedAt
 * - satisfiedInPhase
 */
export default function TenderSummaryTable({
  tenderInfo,
  rows,
  isSpotTender,
  onRefresh = null,
}) {
  const { t } = useTranslation();

  // ===============================================================================================
  // Pagination state
  // ===============================================================================================
  const [first, setFirst] = useState(0);
  const [pageRows, setPageRows] = useState(10);

  // ===============================================================================================
  // Menu anchors
  // ===============================================================================================
  const [anchorAF, setAnchorAF] = useState(null);
  const [anchorCols, setAnchorCols] = useState(null);
  const [anchorViews, setAnchorViews] = useState(null);

  // ===============================================================================================
  // Views and filters state
  // ===============================================================================================
  const {
    metaPair,
    views,
    addMetaPair,
    deleteFilter,
    deleteAllFilters,
    submitFilters,
    resetToDefault,
    applyView,
    saveView,
  } = useTableFilters({
    onSubmit: () => setFirst(0),
  });

  // ===============================================================================================
  // Formatting helpers
  // ===============================================================================================

  /**
   * Safely formats table numeric-like values.
   *
   * @param {any} v - Value to format.
   * @returns {string|number} Dash when nullish, otherwise the original value.
   */
  const fmt = (v) => (v == null ? "-" : v);

  /**
   * Formats satisfiedAt for display.
   *
   * Rules:
   * - null/empty => "-"
   * - "Current" stays "Current"
   * - valid ISO date => stable yyyy-mm-dd hh:mm:ss-ish output
   * - invalid value => original string
   *
   * @param {string|null|undefined} value - Raw satisfiedAt value.
   * @returns {string} Display value.
   */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fmtDate = useCallback((value) => {
    if (!value) return "-";
    if (String(value) === "Current") return t("CurrentPhase");

    try {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return String(value);
      return d.toISOString().replace("T", " ").replace("Z", "");
    } catch {
      return String(value);
    }
  });

  // ===============================================================================================
  // Tender type detection
  // ===============================================================================================
  const computedIsSpotTender =
    typeof isSpotTender === "boolean"
      ? isSpotTender
      : !!(
        tenderInfo?.isSpotTender ??
        tenderInfo?.IsSpotTender ??
        tenderInfo?.isSpot ??
        tenderInfo?.IsSpot
      );

  const showNonSpotCols = !computedIsSpotTender;

  // ===============================================================================================
  // Column labels
  // ===============================================================================================
  const COL = useMemo(() => {
    return {
      Origin: t("Origin") || "Origin",
      Destination: t("Destination") || "Destination",
      TotalQts: t("TotalQts") || "TotalQts",
      TotalBidValue: t("TotalBidValue") || "TotalBidValue",
      MaxBV: t("MaxBV") || "MaxBV",
      MinBV: t("MinBV") || "MinBV",
      TotalLoads: t("TotalLoads") || "TotalLoads",
      MaxLoads: t("MaxLoads") || "MaxLoads",
      MinLoads: t("MinLoads") || "MinLoads",
      MaxETD: t("MaxETD") || "MaxETD",
      MinETD: t("MinETD") || "MinETD",
      IsSatisfied: t("IsSatisfied") || "IsSatisfied",
      SatisfiedAt: t("SatisfiedAt") || "SatisfiedAt",
      SatisfiedInPhase: t("Satisfied InPhase") || "Satisfied InPhase",
    };
  }, [t]);

  // ===============================================================================================
  // Advanced filter metadata
  // ===============================================================================================
  const metaType = useMemo(
    () => [
      { metaName: "origin", metaDisplay: COL.Origin, metaType: "string" },
      {
        metaName: "destination",
        metaDisplay: COL.Destination,
        metaType: "string",
      },
    ],
    [COL.Origin, COL.Destination]
  );

  // ===============================================================================================
  // Client-side filtering
  // ===============================================================================================
  const filteredRows = useMemo(() => {
    let data = Array.isArray(rows) ? [...rows] : [];

    for (const f of metaPair) {
      const raw = f?.value;
      if (!raw) continue;

      const val = String(raw).toLowerCase();
      const op = f?.operation || "Contains";

      if (f.name === "origin") {
        data = data.filter((r) => {
          const s = String(r?.origin ?? "").toLowerCase();
          if (op === "Equals") return s === val;
          return s.includes(val);
        });
      }

      if (f.name === "destination") {
        data = data.filter((r) => {
          const s = String(r?.destination ?? "").toLowerCase();
          if (op === "Equals") return s === val;
          return s.includes(val);
        });
      }
    }

    return data;
  }, [rows, metaPair]);

  // ===============================================================================================
  // Pagination slice
  // ===============================================================================================
  const pagedRows = useMemo(
    () => filteredRows.slice(first, first + pageRows),
    [filteredRows, first, pageRows]
  );

  // ===============================================================================================
  // Visible columns
  // ===============================================================================================
  const singleColumns = useMemo(() => {
    const base = [
      COL.Origin,
      COL.Destination,
      COL.TotalQts,
      COL.TotalBidValue,
      COL.MaxBV,
      COL.MinBV,
      COL.TotalLoads,
      COL.MaxLoads,
      COL.MinLoads,
      COL.MaxETD,
      COL.MinETD,
      COL.IsSatisfied,
      COL.SatisfiedAt,
    ];

    if (!showNonSpotCols) return base;

    return [...base, COL.SatisfiedInPhase];
  }, [COL, showNonSpotCols]);

  const [selectedColumns, setSelectedColumns] = useState(singleColumns);

  // ===============================================================================================
  // Keeps selected columns valid if tender type changes while mounted
  // ===============================================================================================
  useEffect(() => {
    setSelectedColumns((prev) => {
      const allowed = new Set(singleColumns);
      const next = (prev || []).filter((c) => allowed.has(c));
      return next.length ? next : singleColumns;
    });
  }, [singleColumns]);

  // ===============================================================================================
  // Excel export
  // ===============================================================================================
  const exportExcel = useCallback(async () => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("TenderSummary");

    const colDefs = [
      { key: COL.Origin, header: COL.Origin, get: (r) => r?.origin ?? "-" },
      {
        key: COL.Destination,
        header: COL.Destination,
        get: (r) => r?.destination ?? "-",
      },
      {
        key: COL.TotalQts,
        header: COL.TotalQts,
        get: (r) => fmt(r?.totalQuotes),
      },
      {
        key: COL.TotalBidValue,
        header: COL.TotalBidValue,
        get: (r) => fmt(r?.totalBidValue),
      },
      {
        key: COL.MaxBV,
        header: COL.MaxBV,
        get: (r) => fmt(r?.maxBidValue),
      },
      {
        key: COL.MinBV,
        header: COL.MinBV,
        get: (r) => fmt(r?.minBidValue),
      },
      {
        key: COL.TotalLoads,
        header: COL.TotalLoads,
        get: (r) => fmt(r?.totalLoads),
      },
      {
        key: COL.MaxLoads,
        header: COL.MaxLoads,
        get: (r) => fmt(r?.maxLoads),
      },
      {
        key: COL.MinLoads,
        header: COL.MinLoads,
        get: (r) => fmt(r?.minLoads),
      },
      {
        key: COL.MaxETD,
        header: COL.MaxETD,
        get: (r) => fmt(r?.maxEstimatedTransitDays),
      },
      {
        key: COL.MinETD,
        header: COL.MinETD,
        get: (r) => fmt(r?.minEstimatedTransitDays),
      },
      {
        key: COL.IsSatisfied,
        header: COL.IsSatisfied,
        get: (r) =>
          r?.isSatisfied == null ? "-" : r.isSatisfied ? "Yes" : "No",
      },
      {
        key: COL.SatisfiedAt,
        header: COL.SatisfiedAt,
        get: (r) => fmtDate(r?.satisfiedAt),
      },
    ];

    if (showNonSpotCols) {
      colDefs.push({
        key: COL.SatisfiedInPhase,
        header: COL.SatisfiedInPhase,
        get: (r) => r?.satisfiedInPhase ?? "-",
      });
    }

    const exportCols = colDefs.filter((c) => selectedColumns.includes(c.key));

    sheet.addRow(exportCols.map((c) => c.header));

    (filteredRows || []).forEach((r) => {
      sheet.addRow(exportCols.map((c) => c.get(r)));
    });

    try {
      sheet.getRow(1).font = { bold: true };
    } catch {
      // No action is required when styling fails.
    }

    const buffer = await workbook.xlsx.writeBuffer();

    saveAs(
      new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      "TenderSummary.xlsx"
    );
  }, [COL.Origin, COL.Destination, COL.TotalQts, COL.TotalBidValue, COL.MaxBV, COL.MinBV, COL.TotalLoads, COL.MaxLoads, COL.MinLoads, COL.MaxETD, COL.MinETD, COL.IsSatisfied, COL.SatisfiedAt, COL.SatisfiedInPhase, showNonSpotCols, filteredRows, fmtDate, selectedColumns]);

  // ===============================================================================================
  // Render
  // ===============================================================================================
  return (
    <div className="tenderSummary-wrapper">
      <TableHeader
        filterCount={metaPair.length}
        onRefresh={onRefresh}
        onExport={exportExcel}
        onOpenViews={(e) => setAnchorViews(e.currentTarget)}
        onOpenFilters={(e) => setAnchorAF(e.currentTarget)}
        onOpenColumns={(e) => setAnchorCols(e.currentTarget)}
      />

      <ColumnMenu
        anchorEl={anchorCols}
        open={!!anchorCols}
        onClose={() => setAnchorCols(null)}
        singleColumns={singleColumns}
        selectedColumns={selectedColumns}
        onChangeSelected={setSelectedColumns}
        columnGroups={[]}
      />

      <ViewsMenu
        anchorEl={anchorViews}
        open={!!anchorViews}
        onClose={() => setAnchorViews(null)}
        viewsMap={views}
        onSelectDefault={() => {
          resetToDefault();
          setAnchorViews(null);
        }}
        onSelectView={(view) => {
          applyView(view);
          setAnchorViews(null);
        }}
        onCreateNewViewClick={() => {
          setAnchorViews(null);
          setAnchorAF(document.getElementById("advFilter"));
        }}
      />

      <AdvanceFilter
        metaPair={metaPair}
        metaType={metaType}
        onAddMetaPair={addMetaPair}
        onSubmitFilters={submitFilters}
        onDeleteFilter={deleteFilter}
        onDeleteAllFilters={deleteAllFilters}
        anchorAF={anchorAF}
        setAnchorAF={setAnchorAF}
        onSaveFilter={(viewName) => saveView(viewName)}
        views={views}
        onToasts={() => { }}
      />

      <div className="tenderSummary-table-container">
        <table className="tenderSummary">
          <thead>
            <tr>
              {selectedColumns.includes(COL.Origin) && (
                <th
                  style={headerCellStyle()}
                  className="tenderSummary-header-cell col-origin"
                >
                  {COL.Origin}
                </th>
              )}

              {selectedColumns.includes(COL.Destination) && (
                <th
                  style={headerCellStyle()}
                  className="tenderSummary-header-cell col-destination"
                >
                  {COL.Destination}
                </th>
              )}

              {selectedColumns.includes(COL.TotalQts) && (
                <th
                  style={headerCellStyle()}
                  className="tenderSummary-header-cell col-num"
                >
                  {COL.TotalQts}
                </th>
              )}

              {selectedColumns.includes(COL.TotalBidValue) && (
                <th
                  style={headerCellStyle()}
                  className="tenderSummary-header-cell col-num"
                >
                  {COL.TotalBidValue}
                </th>
              )}

              {selectedColumns.includes(COL.MaxBV) && (
                <th
                  style={headerCellStyle()}
                  className="tenderSummary-header-cell col-num"
                >
                  {COL.MaxBV}
                </th>
              )}

              {selectedColumns.includes(COL.MinBV) && (
                <th
                  style={headerCellStyle()}
                  className="tenderSummary-header-cell col-num"
                >
                  {COL.MinBV}
                </th>
              )}

              {selectedColumns.includes(COL.TotalLoads) && (
                <th
                  style={headerCellStyle()}
                  className="tenderSummary-header-cell col-num"
                >
                  {COL.TotalLoads}
                </th>
              )}

              {selectedColumns.includes(COL.MaxLoads) && (
                <th
                  style={headerCellStyle()}
                  className="tenderSummary-header-cell col-num"
                >
                  {COL.MaxLoads}
                </th>
              )}

              {selectedColumns.includes(COL.MinLoads) && (
                <th
                  style={headerCellStyle()}
                  className="tenderSummary-header-cell col-num"
                >
                  {COL.MinLoads}
                </th>
              )}

              {selectedColumns.includes(COL.MaxETD) && (
                <th
                  style={headerCellStyle()}
                  className="tenderSummary-header-cell col-num"
                >
                  {COL.MaxETD}
                </th>
              )}

              {selectedColumns.includes(COL.MinETD) && (
                <th
                  style={headerCellStyle()}
                  className="tenderSummary-header-cell col-num"
                >
                  {COL.MinETD}
                </th>
              )}

              {selectedColumns.includes(COL.IsSatisfied) && (
                <th
                  style={headerCellStyle()}
                  className="tenderSummary-header-cell col-satisfied"
                >
                  {COL.IsSatisfied}
                </th>
              )}

              {selectedColumns.includes(COL.SatisfiedAt) && (
                <th
                  style={headerCellStyle()}
                  className="tenderSummary-header-cell col-date"
                >
                  {COL.SatisfiedAt}
                </th>
              )}

              {showNonSpotCols &&
                selectedColumns.includes(COL.SatisfiedInPhase) && (
                  <th
                    style={headerCellStyle()}
                    className="tenderSummary-header-cell col-phase"
                  >
                    {COL.SatisfiedInPhase}
                  </th>
                )}
            </tr>
          </thead>

          <tbody>
            {(pagedRows || []).map((r) => (
              <tr key={`sum-lane-${r.laneId}`}>
                {selectedColumns.includes(COL.Origin) && (
                  <td
                    className="tenderSummary-cell col-origin"
                    style={tdStyle({
                      background: "var(--color-grey-light)",
                      fontWeight: "bold",
                    })}
                  >
                    {r.origin ?? "-"}
                  </td>
                )}

                {selectedColumns.includes(COL.Destination) && (
                  <td
                    className="tenderSummary-cell col-destination"
                    style={tdStyle({
                      background: "var(--color-grey-light)",
                      fontWeight: "bold",
                    })}
                  >
                    {r.destination ?? "-"}
                  </td>
                )}

                {selectedColumns.includes(COL.TotalQts) && (
                  <td className="tenderSummary-cell col-num" style={tdStyle()}>
                    {fmt(r.totalQuotes)}
                  </td>
                )}

                {selectedColumns.includes(COL.TotalBidValue) && (
                  <td className="tenderSummary-cell col-num" style={tdStyle()}>
                    {fmt(r.totalBidValue)}
                  </td>
                )}

                {selectedColumns.includes(COL.MaxBV) && (
                  <td className="tenderSummary-cell col-num" style={tdStyle()}>
                    {fmt(r.maxBidValue)}
                  </td>
                )}

                {selectedColumns.includes(COL.MinBV) && (
                  <td className="tenderSummary-cell col-num" style={tdStyle()}>
                    {fmt(r.minBidValue)}
                  </td>
                )}

                {selectedColumns.includes(COL.TotalLoads) && (
                  <td className="tenderSummary-cell col-num" style={tdStyle()}>
                    {fmt(r.totalLoads)}
                  </td>
                )}

                {selectedColumns.includes(COL.MaxLoads) && (
                  <td className="tenderSummary-cell col-num" style={tdStyle()}>
                    {fmt(r.maxLoads)}
                  </td>
                )}

                {selectedColumns.includes(COL.MinLoads) && (
                  <td className="tenderSummary-cell col-num" style={tdStyle()}>
                    {fmt(r.minLoads)}
                  </td>
                )}

                {selectedColumns.includes(COL.MaxETD) && (
                  <td className="tenderSummary-cell col-num" style={tdStyle()}>
                    {fmt(r.maxEstimatedTransitDays)}
                  </td>
                )}

                {selectedColumns.includes(COL.MinETD) && (
                  <td className="tenderSummary-cell col-num" style={tdStyle()}>
                    {fmt(r.minEstimatedTransitDays)}
                  </td>
                )}

                {selectedColumns.includes(COL.IsSatisfied) && (
                  <td
                    className="tenderSummary-cell col-satisfied"
                    style={tdStyle()}
                  >
                    {r.isSatisfied == null ? "-" : r.isSatisfied ? t("Yes") : t("No")}
                  </td>
                )}

                {selectedColumns.includes(COL.SatisfiedAt) && (
                  <td
                    className="tenderSummary-cell col-date"
                    style={tdStyle()}
                  >
                    {fmtDate(r.satisfiedAt)}
                  </td>
                )}

                {showNonSpotCols &&
                  selectedColumns.includes(COL.SatisfiedInPhase) && (
                    <td
                      className="tenderSummary-cell col-phase"
                      style={tdStyle()}
                    >
                      {r.satisfiedInPhase ?? "-"}
                    </td>
                  )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Paginator
        first={first}
        rows={pageRows}
        totalRecords={filteredRows.length}
        rowsPerPageOptions={[10, 20, 50]}
        onPageChange={(e) => {
          setFirst(e.first);
          setPageRows(e.rows);
        }}
        template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
      />
    </div>
  );
}

TenderSummaryTable.propTypes = {
  tenderInfo: PropTypes.object,
  rows: PropTypes.array.isRequired,
  isSpotTender: PropTypes.bool,
  onRefresh: PropTypes.func,
};